import { useCallback, useState } from 'react';
import { applicationPaths } from '@core/http/applicationPaths';
import { useApi } from '@core/http/useApi';
import { formatDateForApi, useNotifications } from '@hwm/ui-lib';
import { downloadFile, getFileFromResponse } from '@shared/utils';
import { httpClient } from '@core/http/httpClient';
import { GraphCustomExportType, GraphExportType, GraphDataGetModel, MainGraphFiltersStateModel } from '@shared/models';
import { t } from '@lingui/macro';

export const useGraphExportDataSource = () => {
  const [loading, setLoading] = useState(false);

  const { handleError } = useNotifications();

  // export formats
  const [getExportFormats, exportFormats, formatsLoading, formatsError] = useApi<GraphExportType[]>(
    applicationPaths.pmZones.exportFormats,
    'get'
  );

  const handleGetExportFormats = useCallback(() => {
    getExportFormats().catch(() => {
      // TODO: uncomment when API is ready
      // handleError(t`There was a problem while trying to get export types`, t`Graph export`);
    });
  }, [
    getExportFormats,
    // TODO: uncomment when API is ready
    // handleError
  ]);

  // export - data/summary
  const handleGetExportData = useCallback(
    (pmzId: number, filters: MainGraphFiltersStateModel, format: GraphExportType) => {
      if (!Number.isFinite(pmzId)) return;

      setLoading(true);

      const params = {
        format: format?.toString() ?? GraphCustomExportType.Unspecified,
        timePeriod: 'Custom',
        interval: filters.interval,
        customFrom: formatDateForApi(filters.range[0]),
        customTo: formatDateForApi(filters.range[1]),
        flowUnit: filters.flowUnit,
        pressureUnit: filters.pressureUnit,
      } as GraphExportType | GraphDataGetModel;

      // httpClient used in that use case ; useApi hook provides response with data only, we need full one with headers as well
      httpClient
        .post(applicationPaths.pmZones.exportData({ id: pmzId }), null, {
          params,
          responseType: 'arraybuffer',
        })
        .then((response) => downloadFile(getFileFromResponse(response)))
        .catch(() => {
          handleError(t`There was a problem while trying to export data file`, t`Graph export`);
        })
        .finally(() => setLoading(false));
    },
    [handleError]
  );

  return {
    getExportFormats: handleGetExportFormats,
    getExportData: handleGetExportData,
    exportFormats,
    loading: formatsLoading,
    exportLoading: loading,
    error: formatsError,
  };
};
